import { Box, Divider, Grid, Typography, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** API */
import AboutUs from 'api/admin/clinic-manage/about.api'

/** COMPONENT */
import InputCheckbox from 'component/Input/InputCheckbox'
import InputTextField from 'component/Input/InputTextField'
import ButtonCustom from 'component/Button/ButtonCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'

/** STYLE */
import { colors } from 'constants/theme'
import * as UseStyles from 'features/admin/clinic-management/style'

export interface ReportSchedulerInterface {
  accountingEmail: string
  monthlyAdvanceReportEnabled: string
  monthlyAdvanceReportDay: number
  monthlyAdvanceReportTime: string
  callbackAccountingEmail: (value: string) => void
  callbackMonthlyAdvanceReportEnabled: (value: string) => void
  callbackMonthlyAdvanceReportDay: (value: number) => void
  callbackMonthlyAdvanceReportTime: (value: string) => void
}

// Options for day of month (0 = last day, 1-28 = specific day)
const dayOptions = [
  { value: 0, label: 'วันสุดท้ายของเดือน' },
  ...Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `วันที่ ${i + 1}` }))
]

// Options for time
const timeOptions = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0')
  return { value: `${hour}:00`, label: `${hour}:00 น.` }
})

export default function FormReportScheduler(props: ReportSchedulerInterface) {
  const { t } = useTranslation()
  const [accountingEmail, setAccountingEmail] = useState('')
  const [monthlyAdvanceReportEnabled, setMonthlyAdvanceReportEnabled] = useState(true)
  const [reportDay, setReportDay] = useState(0)
  const [reportTime, setReportTime] = useState('08:00')
  const [testEmail, setTestEmail] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    setAccountingEmail(props.accountingEmail || '')
  }, [props.accountingEmail])

  useEffect(() => {
    setMonthlyAdvanceReportEnabled(props.monthlyAdvanceReportEnabled === '1')
  }, [props.monthlyAdvanceReportEnabled])

  useEffect(() => {
    setReportDay(props.monthlyAdvanceReportDay || 0)
  }, [props.monthlyAdvanceReportDay])

  useEffect(() => {
    setReportTime(props.monthlyAdvanceReportTime || '08:00')
  }, [props.monthlyAdvanceReportTime])

  const handleEmailChange = (value: string) => {
    setAccountingEmail(value)
    props.callbackAccountingEmail(value)
  }

  const handleEnabledChange = () => {
    const newValue = !monthlyAdvanceReportEnabled
    setMonthlyAdvanceReportEnabled(newValue)
    props.callbackMonthlyAdvanceReportEnabled(newValue ? '1' : '0')
  }

  const handleDayChange = (value: number) => {
    setReportDay(value)
    props.callbackMonthlyAdvanceReportDay(value)
  }

  const handleTimeChange = (value: string) => {
    setReportTime(value)
    props.callbackMonthlyAdvanceReportTime(value)
  }

  const handleSendTestReport = async () => {
    const emailToSend = testEmail || accountingEmail
    if (!emailToSend) {
      notiError(t('REPORT_SCHEDULER.ERROR.NO_EMAIL'))
      return
    }

    setSending(true)
    try {
      const res = await AboutUs.sendAdvanceReportManually(emailToSend)
      if (res.data.success) {
        notiSuccess(t('REPORT_SCHEDULER.SUCCESS.SEND_SUCCESS'), '', null)
        setTestEmail('')
      } else {
        notiError(res.data.message || t('REPORT_SCHEDULER.ERROR.SEND_FAILED'))
      }
    } catch (error: any) {
      notiError(error?.response?.data?.message || t('REPORT_SCHEDULER.ERROR.SEND_FAILED'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="px-4 pt-3 pb-3">
      {/* Header */}
      <UseStyles.TitleFirst className="d-flex align-items-center mb-3">
        {t('REPORT_SCHEDULER.TITLE')}
      </UseStyles.TitleFirst>
      <Typography className="mb-3" sx={{ fontSize: '14px', color: colors.disabledGray }}>
        {t('REPORT_SCHEDULER.DESCRIPTION')}
      </Typography>

      <Divider
        className="mx-0 my-3"
        sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }}
      />

      {/* Enable/Disable */}
      <Box className="mb-3">
        <InputCheckbox
          label={t('REPORT_SCHEDULER.ENABLE_MONTHLY_REPORT')}
          checked={monthlyAdvanceReportEnabled}
          onChange={handleEnabledChange}
        />
        <Typography sx={{ fontSize: '12px', color: colors.disabledGray, ml: 4 }}>
          {t('REPORT_SCHEDULER.ENABLE_DESCRIPTION')}
        </Typography>
      </Box>

      <Divider
        className="mx-0 my-3"
        sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }}
      />

      {/* Accounting Email */}
      <Box className="mb-3">
        <UseStyles.TitleFirst className="d-flex align-items-center mb-2">
          {t('REPORT_SCHEDULER.ACCOUNTING_EMAIL')}
        </UseStyles.TitleFirst>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InputTextField
              label={t('REPORT_SCHEDULER.EMAIL_LABEL')}
              value={accountingEmail}
              onchange={(e: any) => handleEmailChange(e.target.value)}
              placeholder="accounting@company.com"
            />
            <Typography sx={{ fontSize: '12px', color: colors.disabledGray, mt: 1 }}>
              {t('REPORT_SCHEDULER.EMAIL_HELPER')}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider
        className="mx-0 my-3"
        sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }}
      />

      {/* Schedule Info - Configurable */}
      <Box className="mb-3">
        <UseStyles.TitleFirst className="d-flex align-items-center mb-2">
          {t('REPORT_SCHEDULER.SCHEDULE_INFO')}
        </UseStyles.TitleFirst>
        <Typography className="mb-2" sx={{ fontSize: '14px', color: colors.disabledGray }}>
          {t('REPORT_SCHEDULER.SCHEDULE_DESCRIPTION') || 'กำหนดวันและเวลาที่ต้องการส่งรายงานอัตโนมัติ'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <AutocompleteSelect
              labelId="report-day"
              inputLabel={t('REPORT_SCHEDULER.SEND_DATE') || 'วันที่ส่ง'}
              options={dayOptions}
              getOptionLabel={(option: any) => option?.label || ''}
              renderOption={(props: any, option: any) => (
                <Box {...props}>
                  {option?.label}
                </Box>
              )}
              value={dayOptions.find((d) => d.value === reportDay) || dayOptions[0]}
              onchange={(e: any, value: any) => handleDayChange(value?.value ?? 0)}
              disableClearable
              disabled={!monthlyAdvanceReportEnabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutocompleteSelect
              labelId="report-time"
              inputLabel={t('REPORT_SCHEDULER.SEND_TIME') || 'เวลาที่ส่ง'}
              options={timeOptions}
              getOptionLabel={(option: any) => option?.label || ''}
              renderOption={(props: any, option: any) => (
                <Box {...props}>
                  {option?.label}
                </Box>
              )}
              value={timeOptions.find((t) => t.value === reportTime) || timeOptions[8]}
              onchange={(e: any, value: any) => handleTimeChange(value?.value ?? '08:00')}
              disableClearable
              disabled={!monthlyAdvanceReportEnabled}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            backgroundColor: colors.extraLightBlue,
            borderRadius: '8px',
            padding: '16px',
            border: `1px solid ${colors.extraLightBlue}`,
            marginTop: '16px'
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            📌 <strong>{t('REPORT_SCHEDULER.CURRENT_SCHEDULE') || 'กำหนดการปัจจุบัน'}:</strong>{' '}
            {reportDay === 0 ? 'วันสุดท้ายของเดือน' : `วันที่ ${reportDay}`} เวลา {reportTime} น.
          </Typography>
        </Box>
      </Box>

      <Divider
        className="mx-0 my-3"
        sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }}
      />

      {/* Test Send */}
      <Box className="mb-3">
        <UseStyles.TitleFirst className="d-flex align-items-center mb-2">
          {t('REPORT_SCHEDULER.TEST_SEND')}
        </UseStyles.TitleFirst>
        <Typography className="mb-2" sx={{ fontSize: '14px', color: colors.disabledGray }}>
          {t('REPORT_SCHEDULER.TEST_SEND_DESCRIPTION')}
        </Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <InputTextField
              label={t('REPORT_SCHEDULER.TEST_EMAIL_LABEL')}
              value={testEmail}
              onchange={(e: any) => setTestEmail(e.target.value)}
              placeholder={accountingEmail || 'test@example.com'}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonCustom
              onClick={handleSendTestReport}
              disabled={sending || (!testEmail && !accountingEmail)}
              textButton={
                sending ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} color="inherit" />
                    {t('REPORT_SCHEDULER.SENDING')}
                  </Box>
                ) : (
                  t('REPORT_SCHEDULER.SEND_TEST_BUTTON')
                )
              }
              btnStyle={{ height: '40px' }}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider
        className="mx-0 my-3"
        sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }}
      />

      {/* Report Content Info */}
      <Box className="mb-3">
        <UseStyles.TitleFirst className="d-flex align-items-center mb-2">
          {t('REPORT_SCHEDULER.REPORT_CONTENT')}
        </UseStyles.TitleFirst>
        <Box
          sx={{
            backgroundColor: '#FFF8E1',
            borderRadius: '8px',
            padding: '16px',
            border: `1px solid #FFE082`
          }}
        >
          <Typography className="mb-2" sx={{ fontWeight: 500 }}>
            📊 {t('REPORT_SCHEDULER.ADVANCE_BALANCE_REPORT')}
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '20px', color: colors.textPrimary }}>
            <li>{t('REPORT_SCHEDULER.CONTENT_1')}</li>
            <li>{t('REPORT_SCHEDULER.CONTENT_2')}</li>
            <li>{t('REPORT_SCHEDULER.CONTENT_3')}</li>
            <li>{t('REPORT_SCHEDULER.CONTENT_4')}</li>
          </ul>
        </Box>
      </Box>
    </div>
  )
}
